import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { LeadsSavedToEntitiesQuery } from "../../../generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const leadsSavedToEntitiesQuery = graphql(`
  query leadsSavedToEntities($ids: [String!]!) {
    leadsSavedToEntities(entityIds: $ids) {
      id
      name
    }
  }
`);

/**
 * Returns the leads that are saved to all of the provided entities (e.g., notices, buyers, frameworks).
 *
 * The {@param entityIds} argument accepts multiple entity IDs and this hook returns only the leads
 * that are associated with every one of those entities (i.e., an intersection of saved leads).
 * When a single ID is provided, this effectively behaves like a single-entity query; providing
 * multiple IDs narrows the result set to leads common to all of them.
 */
export function useLeadsSavedToEntities(
  entityIds: string[],
  options?: UseQueryOptions<
    LeadsSavedToEntitiesQuery,
    unknown,
    LeadsSavedToEntitiesQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["leadsSavedToEntities", entityIds],
    leadsSavedToEntitiesQuery,
    [{ ids: entityIds }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: (options?.enabled ?? true) && entityIds.length > 0, // Only enable the query if we have entity IDs to look up
    },
  );

  return {
    data: data?.leadsSavedToEntities,
    ...rest,
  };
}
